import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Link } from "react-router-dom";
import Navbar from "../../navbar";
import Header from "../../header";
import Pagination from "../dashboard/appPagination";
import RejectDialog from "./rejectDialog/rejectDialog";
import StatusConfirmDialog, {
  STATUS_LABELS,
  getStatusRoute,
} from "./statusConfirmDialog/statusConfirmDialog";
import AuditTrailModal from "./auditTrail/AuditTrailModal";
import {
  fetchApplications,
  fetchApplicationAuditTrail,
  updateApplication,
  clearApplications,
  clearAuditTrail,
  isThunkFulfilled,
} from "../../../store/reducers/applicationSlice";
import { createNotification } from "../../../store/reducers/notificationSlice";
import { handleFilesDownload } from "../../../store/reducers/fileSlice";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import iconSearch from "../../../assets/media/icon-search.svg";
import iconMenuDots from "../../../assets/media/icon-menu-dots.svg";
import { hasRole, ROLES } from "../../../utility/rbac";
import { getNotificationTemplate } from "../../../utility/notificationTemplates";
import {
  buildLifecycleDateParams,
  formatLifecycleDate,
  getRejectionDate,
} from "../../../utility/lifecycleDates";

const columnHelper = createColumnHelper();

const appRowId = (row) => row?.applicationId || row?.id;

const pickReasonFromRow = (row) =>
  (row?.remarks && String(row.remarks).trim()) ||
  row?.discrepancy?.other?.value ||
  row?.discrepancies?.other?.value ||
  "";

const pickReasonFromAuditLogs = (logs = []) => {
  const withRemarks = (logs || []).filter(
    (log) => log?.remarks && String(log.remarks).trim()
  );
  if (!withRemarks.length) return "";

  // Newest first (API usually returns newest first; sort defensively)
  const sorted = [...withRemarks].sort(
    (a, b) =>
      new Date(b.created_at || 0).getTime() -
      new Date(a.created_at || 0).getTime()
  );

  const rejectedLog = sorted.find((log) => {
    const to = String(
      log.to_state_name || log.to_state || log.toState || ""
    ).toLowerCase();
    return to.includes("rejected") || log.to_state_id === 5;
  });

  // Prefer latest rejection remarks, else latest remarks on any action (e.g. UPDATED)
  return String((rejectedLog || sorted[0]).remarks).trim();
};

const RejectedApp = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const authToken = user.access_token;

  const applications = useSelector((state) => state.applications);
  const pagination = useSelector((state) => state.applications.pagination);
  const applicationLoading = useSelector((state) => state.applications.fetchLoading);
  const getallUserdata = useSelector((state) => state.userdata.getalluserdata);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchBy, setSearchBy] = useState("applicationId");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filteredApplication, setFilteredApplication] = useState([]);
  const [downloadingIds, setDownloadingIds] = useState([]);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [selectedRejectItem, setSelectedRejectItem] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [showAuditTrail, setShowAuditTrail] = useState(false);
  const [auditApplication, setAuditApplication] = useState(null);
  // Remarks often live on audit logs; keep local overrides after edit
  const [reasonByAppId, setReasonByAppId] = useState({});

  const handleOpenAuditTrail = (item) => {
    setAuditApplication(item || null);
    setShowAuditTrail(true);
  };

  const handleCloseAuditTrail = () => {
    setShowAuditTrail(false);
    setAuditApplication(null);
  };

  const [showReasonModal, setShowReasonModal] = useState(false);

  // Clear applications when component mounts to ensure fresh data
  useEffect(() => {
    dispatch(clearApplications());
  }, [dispatch]);
  const [selectedReason, setSelectedReason] = useState("");

  // Fetch applications when date filter changes
  useEffect(() => {
    let queryParams = {
      page: currentPage,
      limit: itemsPerPage || 10,
      state: "rejected"
    };

    if (searchQuery && searchQuery.trim() !== '') {
      queryParams[searchBy] = searchQuery.trim();
    }

      Object.assign(
        queryParams,
        buildLifecycleDateParams("rejected", startDate, endDate)
      );

    dispatch(fetchApplications({ accessToken: authToken, queryParams }));
  }, [authToken, dispatch, startDate, endDate, currentPage, searchQuery, itemsPerPage, searchBy]);

  // Merge user data and filter applications
  useEffect(() => {

    const applicationsArray = Array.isArray(applications.data)
      ? applications.data
      : applications.data?.applications || applications.data?.data || [];

    if (applicationsArray.length > 0) {
      const mergedData = applicationsArray.map((appItem) => {
        const userItem = getallUserdata?.find?.(
          (userData) => userData.id === appItem.user_id
        );
        if (userItem) {
          return {
            ...userItem,
            ...appItem,
            applicationId: appItem.id,
            userId: userItem.id,
            remarks: appItem.remarks,
          };
        }
        return { ...appItem, applicationId: appItem.id };
      });

      // Since we're using server-side filtering, we only need to filter by company_id
      const filteredData = mergedData.filter(
        (item) => (item.company_id === user.user.company_id || user.user.is_admin || ["reviewer", "viewer"].includes(user.user.user_role?.toLowerCase()))
      );

      // Debug: Log discrepancy data structure

      setFilteredApplication(filteredData);
    } else {
      setFilteredApplication([]);
    }
  }, [applications.data, getallUserdata, user.user.company_id, user.user.is_admin]);

  const handleSearchInputChange = (e) => {
    let value = e.target.value;
    if (searchBy === "applicationId") {
      value = value.replace(/^gb-?/i, "").replace(/\D/g, "");
    }
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleSearchByChange = (e) => {
    setSearchBy(e.target.value);
    setSearchQuery("");
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing items per page
  };

  const getTotalPages = () => {
    // Use server-side pagination metadata
    if (pagination?.totalPages) {
      return pagination.totalPages;
    }
    // Fallback to client-side calculation
    const validItemsPerPage = itemsPerPage || 10;
    return Math.ceil(filteredApplication.length / validItemsPerPage);
  };

  const handleDownload = useCallback(async (item) => {
    const { applicationId, userId } = item;
    const id = applicationId || item.id;

    // Prevent multiple downloads - check if already downloading
    if (downloadingIds.includes(id)) {
      return;
    }

    setDownloadingIds((prev) => [...prev, id]);

    try {
      await handleFilesDownload({ userId, applicationId: id, authToken });
    } catch (error) {
      console.error("Download failed", error);
    } finally {
      setDownloadingIds((prev) => prev.filter((downloadId) => downloadId !== id));
    }
  }, [authToken, downloadingIds]);

  const getRejectReason = useCallback(
    (row) => {
      const id = appRowId(row);
      if (id != null && reasonByAppId[id]) return reasonByAppId[id];
      return pickReasonFromRow(row);
    },
    [reasonByAppId]
  );

  const loadRemarksFromAudit = useCallback(
    async (item) => {
      const id = appRowId(item);
      if (!id || !authToken) return "";
      // Do not filter by action — reason edits may land as UPDATED, not STATE_CHANGED
      const result = await dispatch(
        fetchApplicationAuditTrail({
          accessToken: authToken,
          id,
          page: 1,
          limit: 50,
        })
      );
      if (!isThunkFulfilled(result)) return "";
      return pickReasonFromAuditLogs(result.payload?.logs);
    },
    [authToken, dispatch]
  );

  const handleOpenReject = async (item) => {
    setSelectedRejectItem(item);
    const existing = getRejectReason(item);
    setRejectReason(existing);
    setShowRejectModal(true);

    if (!existing) {
      const fromAudit = await loadRemarksFromAudit(item);
      if (fromAudit) {
        setRejectReason(fromAudit);
        setReasonByAppId((prev) => ({ ...prev, [appRowId(item)]: fromAudit }));
      }
    }
  };

  const handleCloseReject = () => {
    setShowRejectModal(false);
    setSelectedRejectItem(null);
    setRejectReason("");
  };

  const requestStatusChange = (item, state, discrepancies = null) => {
    setPendingStatusChange({ item, state, discrepancies });
    setShowStatusModal(true);
  };

  const handleCloseStatusModal = () => {
    setShowStatusModal(false);
    setPendingStatusChange(null);
  };

  const handleConfirmStatusChange = (remarks = "") => {
    if (!pendingStatusChange) return;
    const { item, state, discrepancies } = pendingStatusChange;
    handleCloseStatusModal();
    handleChangeStatus(
      { ...item, remarks: remarks || undefined },
      state,
      discrepancies
    );
  };

  const refreshRejectedList = () => {
    const validItemsPerPage =
      typeof itemsPerPage === "number" && itemsPerPage > 0 ? itemsPerPage : 10;
    let queryParams = {
      page: currentPage,
      limit: validItemsPerPage,
      state: "rejected",
    };
    if (searchQuery && searchQuery.trim() !== "") {
      queryParams[searchBy] = searchQuery.trim();
    }
    Object.assign(
      queryParams,
      buildLifecycleDateParams("rejected", startDate, endDate)
    );
    dispatch(fetchApplications({ accessToken: authToken, queryParams }));
  };

  const handleConfirmReject = async (remarks = "") => {
    if (!selectedRejectItem || !remarks.trim()) return;
    const item = selectedRejectItem;
    const note = remarks.trim();
    const id = appRowId(item);
    handleCloseReject();

    // API only attaches remarks to real status-change audit rows.
    // Same-state "rejected → rejected" is a no-op, so briefly leave and
    // return to rejected with the new remarks to record them on the audit trail.
    const leaveRes = await dispatch(
      updateApplication({
        id,
        state: 1, // in_process (intermediate)
        remarks: note,
        accessToken: authToken,
        silent: true,
        skipNotification: true,
      })
    );
    if (!isThunkFulfilled(leaveRes)) return;

    const result = await dispatch(
      updateApplication({
        id,
        state: 5, // rejected
        remarks: note,
        accessToken: authToken,
        successMessage: "Rejection reason updated!",
        skipNotification: true,
      })
    );
    if (!isThunkFulfilled(result)) {
      // Ensure app is not left in the intermediate status
      await dispatch(
        updateApplication({
          id,
          state: 5,
          remarks: note,
          accessToken: authToken,
          silent: true,
          skipNotification: true,
        })
      );
      return;
    }

    // Force Audit Trail modal to refetch next time it opens
    dispatch(clearAuditTrail());

    setReasonByAppId((prev) => ({ ...prev, [id]: note }));
    setFilteredApplication((prev) =>
      prev.map((row) =>
        appRowId(row) === id ? { ...row, remarks: note } : row
      )
    );
    refreshRejectedList();
  };

  const handleChangeStatus = async (item, state, discrepancies = null) => {
    const updatePayload = {
      id: item?.applicationId || item?.id,
      state: state,
      remarks: item?.remarks || undefined,
      accessToken: authToken,
    };
    // Only send descripencie when we actually have discrepancy data
    if (discrepancies != null) {
      updatePayload.descripencie = discrepancies;
    }

    const result = await dispatch(updateApplication(updatePayload));
    if (!isThunkFulfilled(result)) return;

    const validItemsPerPage = (typeof itemsPerPage === 'number' && itemsPerPage > 0) ? itemsPerPage : 10;

    let queryParams = {
      page: currentPage,
      limit: validItemsPerPage,
      search: searchQuery,
      state: 'rejected'
    };

    if (startDate && endDate) {
      queryParams.start_date = startDate.toISOString();
      queryParams.end_date = endDate.toISOString();
    }

    const template = getNotificationTemplate(state, {
      ...item,
      userName: item.userName || item.user_name || 'User',
      discrepancies: discrepancies
    });

    dispatch(createNotification({
      message: template.message,
      subject: template.subject,
      htmlMailContent: template.html,
      sendToCompanyAdmins: true,
      userId: item.userId || item.user_id,
      accessToken: authToken,
      is_admin: false,
    }));

    const destination = getStatusRoute(state);
    if (destination) {
      navigate(destination);
    } else {
      dispatch(fetchApplications({ accessToken: authToken, queryParams }));
    }
  };

  // Handle reason click to show modal (remarks from row, override, or audit trail)
  const handleReasonClick = async (row) => {
    const existing = getRejectReason(row);
    setSelectedReason(existing || "Loading…");
    setShowReasonModal(true);

    if (existing) return;

    const fromAudit = await loadRemarksFromAudit(row);
    if (fromAudit) {
      setSelectedReason(fromAudit);
      setReasonByAppId((prev) => ({ ...prev, [appRowId(row)]: fromAudit }));
    } else {
      setSelectedReason("No reason provided");
    }
  };

  // Since we're using server-side pagination, we don't need client-side pagination
  const paginatedData = useMemo(() => {
    return filteredApplication
      .sort((a, b) => b.applicationId - a.applicationId);
  }, [filteredApplication]);

  // React Table columns
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "serial",
        header: "S.NO.",
        cell: (info) => (currentPage - 1) * itemsPerPage + info.row.index + 1,
      }),
      columnHelper.accessor("applicationId", {
        header: "Application No.",
        cell: (info) => `GB-${info.getValue()}`,
      }),
      columnHelper.display({
        id: "applicationName",
        header: "Application Name",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong>{row.name?.length > 50 ? `${row.name.substring(0, 50)}...` : row.name}</strong>
              <span>{row.categoryName}</span>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "submittedBy",
        header: "Submitted By",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong>{row.companyName || row.company_name || 'N/A'}</strong>
              <span>{row.companyEmail || row.company_email || 'N/A'}</span>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "submittedFor",
        header: "Submitted For",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong>{row.userName || row.user_name || 'N/A'}</strong>
              <span>{row.userEmail || row.user_email || row.email || 'N/A'}</span>
            </div>
          );
        },
      }),

      columnHelper.display({
        id: "rejectionDate",
        header: "Rejection Date",
        cell: (info) => (
          <span className="dateText">
            {formatLifecycleDate(getRejectionDate(info.row.original))}
          </span>
        ),
      }),
      columnHelper.display({
        id: "reason",
        header: "Reason",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div
              className="reasonText clickable-reason"
              onClick={() => handleReasonClick(row)}
              style={{ cursor: "pointer" }}
            >
              <span>View Reason</span>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardActions">
              <div className="dropdown d-inline-flex">
                <button
                  className="application-dropdown dropdown-toggle border-0 bg-transparent"
                  type="button"
                  id="dropdownMenuButton"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <img src={iconMenuDots} alt="Menu Dots" />
                </button>
                <ul
                  className="dropdown-menu"
                  aria-labelledby="dropdownMenuButton"
                >
                  <li>
                    {downloadingIds.includes(row.applicationId || row.id) ? (
                      <span className="dropdown-item disabled" style={{ cursor: 'not-allowed', opacity: 0.6, pointerEvents: 'none' }}>
                        Downloading...
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="dropdown-item"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const id = row.applicationId || row.id;
                          if (!downloadingIds.includes(id)) {
                            handleDownload(row);
                          }
                        }}
                        style={{ cursor: 'pointer', border: 'none', background: 'transparent', width: '100%', textAlign: 'left', padding: '0 16px' }}
                      >
                        Download
                      </button>
                    )}
                  </li>
                  <li>
                    <button
                      type="button"
                      className="dropdown-item"
                      onClick={() => handleOpenAuditTrail(row)}
                    >
                      Audit Trail
                    </button>
                  </li>
                  {!hasRole(user, ROLES.VIEWER) && (
                    <>
                      {/* <li>
                        <Link
                          onClick={() => handleChangeStatus(row, 3)}
                          className="dropdown-item"
                        >
                          Pending For Approval
                        </Link>
                      </li> */}
                      <li>
                        <Link
                          onClick={() => requestStatusChange(row, 4)}
                          className="dropdown-item"
                        >
                          Move to Approved
                        </Link>
                      </li>
                      <li>
                        <Link
                          onClick={() => requestStatusChange(row, 1)}
                          className="dropdown-item"
                        >
                          Move to In process
                        </Link>
                      </li>
                      {/* <li>
                        <Link
                          onClick={() => handleOpenReject(row)}
                          className="dropdown-item"
                        >
                          Edit Reason
                        </Link>
                      </li> */}
                    </>
                  )}
                </ul>
              </div>
            </div>
          );
        },
      }),
    ],
    [
      downloadingIds,
      navigate,
      getallUserdata,
      handleDownload,
      handleReasonClick,
      handleOpenReject,
      currentPage,
      itemsPerPage,
    ]
  );

  const table = useReactTable({
    data: paginatedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="mainApp">
      <Navbar />
      <Header />
      <div className="wrapper">
        <div className="boxAppBoard boxWhite">
          {/* Header with search + date filter */}
          <div className="boardHeader">
            <div className="boardHeadLeft">
              <h2>Rejected Applications</h2>
            </div>
            <div className="boardHeadRight">
              <div className="">
                <select onChange={handleSearchByChange} value={searchBy}>
                  <option value="applicationId">Search by Application ID</option>
                  <option value="companyName">Search by Company Name</option>
                  <option value="username">Search by Employee Name</option>
                  {/* <option value="email">Search by Email </option> */}
                </select>
              </div>
              <div className="searchForm">
                <input
                  type="text"
                  placeholder={searchBy === "applicationId" ? "GB-XXX" : "Search"}
                  onChange={handleSearchInputChange}
                  value={searchBy === "applicationId" && searchQuery ? `GB-${searchQuery}` : searchQuery}
                  className="searchInput"
                />
                <button type="button" className="searchBtn">
                  <img src={iconSearch} alt="search" />
                </button>
              </div>
              <div className="datePicker">
                <DatePicker
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => {
                    const [start, end] = update;
                    setStartDate(start);
                    setEndDate(end);
                    setCurrentPage(1);
                  }}
                  isClearable={true}
                  placeholderText="Select Date Range"
                  className="date-range-input"
                  dateFormat="yyyy-MM-dd"
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="boardContent table-responsive" style={{ position: 'relative' }}>
            {/* Loader overlay for table section */}
            {applicationLoading && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 10,
                  borderRadius: '4px'
                }}
              >
                <div className="text-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2 mb-0">Loading applications...</p>
                </div>
              </div>
            )}
            <table className="table">
              <thead className="">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr className="" key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id}>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody>
                {applicationLoading ? (
                  <tr>
                    <td colSpan={table.getHeaderGroups()?.[0]?.headers.length} style={{ textAlign: "center", padding: "20px" }}>
                      <div className="spinner-border spinner-border-sm text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : table.getRowModel().rows.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr className="" key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={table.getHeaderGroups()?.[0]?.headers.length} style={{ textAlign: "center", padding: "20px" }}>
                      {applicationLoading ? "Loading..." : "Result not found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <RejectDialog
            open={showRejectModal}
            reason={rejectReason}
            setReason={setRejectReason}
            onReject={handleConfirmReject}
            onCancel={handleCloseReject}
          />

          <StatusConfirmDialog
            open={showStatusModal}
            title="Are you sure you want to change status?"
            message={
              pendingStatusChange
                ? `Change status to "${STATUS_LABELS[pendingStatusChange.state] || pendingStatusChange.state}"?`
                : ""
            }
            onConfirm={handleConfirmStatusChange}
            onCancel={handleCloseStatusModal}
          />

          <AuditTrailModal
            open={showAuditTrail}
            onClose={handleCloseAuditTrail}
            applicationId={auditApplication?.applicationId || auditApplication?.id}
            applicationNo={
              auditApplication
                ? `GB-${auditApplication.applicationId || auditApplication.id}`
                : ""
            }
            submittedBy={
              auditApplication?.companyName ||
              auditApplication?.company_name ||
              "—"
            }
            submittedFor={
              auditApplication?.userName ||
              auditApplication?.user_name ||
              "—"
            }
            accessToken={authToken}
          />

          {/* Reason Modal */}
          {showReasonModal && (
            <div className="modal show d-block" tabIndex="-1" role="dialog">
              <div className="modal-dialog reason-modal" role="document">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Rejection Reason</h5>
                  </div>
                  <div className="modal-body">
                    <p>{selectedReason}</p>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowReasonModal(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Pagination */}
          {getTotalPages}

          <Pagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={getTotalPages}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            totalItems={pagination?.totalItems || filteredApplication.length}
          />

        </div>
      </div>
    </div>
  );
};

export default RejectedApp;